export type LegendExportItem = {
  name: string;
  color: string;
  hidden?: boolean;
  iconType?: 'circle' | 'rect';
};

export type LegendExportConfig = {
  items: LegendExportItem[];
  position?: { y?: number };
  fontFamily?: string;
  textColor?: string;
};

/**
 * Appends an SVG legend into the provided SVG element. Used for export-only legends.
 */
export function appendLegendToSvg(svg: SVGSVGElement, cfg: LegendExportConfig) {
  const vb = svg.viewBox?.baseVal;
  const svgWidth =
    vb?.width || Number(svg.getAttribute('width')) || svg.clientWidth || 800;
  const padding = 10;
  const startY = cfg.position?.y ?? 16;
  const lineHeight = 18;

  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  const fontFamily =
    cfg.fontFamily || 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const textColor = cfg.textColor || '#222';
  group.setAttribute('font-family', fontFamily);
  group.setAttribute('font-size', '12');
  group.setAttribute('fill', textColor);

  // Build lines to support wrapping and center alignment
  const availableWidth = svgWidth - padding * 2;
  const itemGap = 16;
  const itemsWithWidth = cfg.items.map((item) => {
    const label = item.name ?? '';
    // rough text width estimation (approx char width 7px at 12px font)
    const estTextWidth = Math.max(30, Math.ceil(label.length * 7));
    const itemWidth = 10 + 6 + 6 + estTextWidth; // circle + gap + text
    return { item, label, itemWidth };
  });

  const lines: {
    items: { item: LegendExportItem; label: string; itemWidth: number }[];
    lineWidth: number;
  }[] = [];
  let current: {
    item: LegendExportItem;
    label: string;
    itemWidth: number;
  }[] = [];
  let currentWidth = 0;
  for (const iw of itemsWithWidth) {
    const toAdd = current.length === 0 ? iw.itemWidth : iw.itemWidth + itemGap;
    if (currentWidth + toAdd > availableWidth && current.length > 0) {
      const lineWidth =
        current.reduce((acc, it) => acc + it.itemWidth, 0) +
        itemGap * (current.length - 1);
      lines.push({ items: current, lineWidth });
      current = [iw];
      currentWidth = iw.itemWidth;
    } else {
      current.push(iw);
      currentWidth += toAdd;
    }
  }
  if (current.length > 0) {
    const lineWidth =
      current.reduce((acc, it) => acc + it.itemWidth, 0) +
      itemGap * (current.length - 1);
    lines.push({ items: current, lineWidth });
  }

  let cursorY = startY;
  for (const line of lines) {
    const lineStartX =
      padding + Math.max(0, (availableWidth - line.lineWidth) / 2);
    let cursorX = lineStartX;
    for (let i = 0; i < line.items.length; i++) {
      const { item, label, itemWidth } = line.items[i];
      const itemGroup = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'g'
      );
      itemGroup.setAttribute('transform', `translate(${cursorX}, ${cursorY})`);
      if (item.hidden) itemGroup.setAttribute('opacity', '0.3');

      // Create icon based on type
      const iconType = item.iconType || 'circle';
      let icon: SVGElement;

      if (iconType === 'rect') {
        icon = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        icon.setAttribute('x', '1');
        icon.setAttribute('y', '1');
        icon.setAttribute('width', '10');
        icon.setAttribute('height', '10');
        icon.setAttribute('rx', '2');
        icon.setAttribute('fill', item.color || '#888');
      } else {
        icon = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        icon.setAttribute('cx', '6');
        icon.setAttribute('cy', '6');
        icon.setAttribute('r', '5');
        icon.setAttribute('fill', item.color || '#888');
        icon.setAttribute('stroke', item.color || '#888');
      }

      const text = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      );
      text.setAttribute('x', '18');
      text.setAttribute('y', '10');
      text.textContent = label;

      itemGroup.appendChild(icon);
      itemGroup.appendChild(text);
      group.appendChild(itemGroup);

      cursorX += itemWidth + (i < line.items.length - 1 ? itemGap : 0);
    }
    cursorY += lineHeight;
  }

  svg.appendChild(group);
}
